import { parsePhoneNumberWithError } from "libphonenumber-js";
import { z } from "zod";
import { getMaxStringLengthMessage, getRequiredFieldMessage } from ".";

/**
 * Creates a required string schema with trimming and length validation
 * @param displayName - Name to use in validation error messages
 * @param maxLength - Maximum allowed string length after trimming
 * @returns Zod schema for a required, trimmed string with length validation
 */
export const createRequiredStringSchema = (displayName: string, maxLength: number) =>
  z
    .string()
    .trim()
    .min(1, { error: getRequiredFieldMessage(displayName) })
    .max(maxLength, { error: getMaxStringLengthMessage({ displayName, maxLength }) });

/**
 * Creates an optional string schema with trimming and length validation
 * @param displayName - Name to use in validation error messages
 * @param maxLength - Maximum allowed string length after trimming
 * @returns Zod schema for an optional, trimmed string with length validation
 */
export const createOptionalStringSchema = (displayName: string, maxLength: number) =>
  z
    .string()
    .trim()
    .max(maxLength, { error: getMaxStringLengthMessage({ displayName, maxLength }) });

/**
 * Creates an optional email schema that accepts empty string or valid email
 * @param displayName - Name to use in validation error messages
 * @param maxLength - Maximum allowed email length
 * @returns Zod schema for optional email validation
 */
// Optional email: https://github.com/colinhacks/zod/issues/2513#issuecomment-1732405993
export const createOptionalEmailSchema = (displayName: string, maxLength = 50) =>
  z.union([
    z.literal(""),
    z.email().max(maxLength, {
      message: getMaxStringLengthMessage({ displayName, maxLength }),
    }),
  ]);

/**
 * Creates a required email schema with trimming and validation
 * @param displayName - Name to use in validation error messages
 * @param maxLength - Maximum allowed email length (defaults to 50)
 * @returns Zod schema for a required, trimmed email with length validation
 *
 * @example
 * // Create required email schema
 * const emailSchema = createRequiredEmailSchema("email")
 *
 * // Invalid inputs fail validation:
 * emailSchema.parse("") // => Error: Email is required
 * emailSchema.parse("notanemail") // => Error: Please enter a valid email
 */
export const createRequiredEmailSchema = (displayName: string, maxLength = 50) =>
  z.email().max(maxLength, {
    message: getMaxStringLengthMessage({ displayName, maxLength }),
  });

/**
 * Creates an optional phone number schema that validates US phone numbers
 * @returns Zod schema that transforms valid phone numbers to E.164 format
 * @example
 * // Valid input: "(212) 555-1234" -> Output: "+12125551234"
 * // Invalid input: "123" -> Validation error
 */
export const createOptionalPhoneSchema = () =>
  z
    .string()
    .optional()
    .transform((value, ctx) => {
      if (!value) return null;

      try {
        const phoneNumber = parsePhoneNumberWithError(value, {
          defaultCountry: "US",
        });

        if (!phoneNumber?.isValid()) {
          ctx.addIssue({
            code: "custom",
            message: "Please enter a valid phone number.",
          });
          return z.NEVER;
        }

        return phoneNumber.format("E.164");
      } catch {
        ctx.addIssue({
          code: "custom",
          message: "Please enter a valid phone number.",
        });
        return z.NEVER;
      }
    });

/**
 * Creates a required integer schema with validation for min/max bounds
 * @param options - Configuration options
 * @param options.displayName - Name to use in validation error messages
 * @param options.max - Maximum allowed value
 * @param options.min - Minimum allowed value
 * @returns Zod schema for required integer with range validation
 *
 * @example
 * // Create schema for age field (must be between 0-120)
 * const ageSchema = createRequiredIntegerSchema({
 *   displayName: "Age",
 *   min: 0,
 *   max: 120
 * });
 *
 * // Invalid inputs fail validation:
 * ageSchema.parse("") // => Error: Age is required
 * ageSchema.parse("12.5") // => Error: Age must be a whole number
 * ageSchema.parse("150") // => Error: Age cannot exceed 120
 */
export const createRequiredIntegerSchema = ({
  displayName,
  max,
  min,
}: {
  displayName: string;
  max: number;
  min: number;
}) => {
  return z.union([z.string(), z.undefined()]).transform((val, ctx) => {
    // Handle undefined and empty strings - these should show "required" error
    if (!val || (typeof val === "string" && val.trim() === "")) {
      ctx.addIssue({
        code: "custom",
        message: getRequiredFieldMessage(displayName),
      });
      return z.NEVER;
    }

    // At this point, val is a non-empty string
    const num = Number(val);
    if (isNaN(num)) {
      ctx.addIssue({
        code: "custom",
        message: getRequiredFieldMessage(displayName),
      });
      return z.NEVER;
    }
    if (!Number.isInteger(num)) {
      ctx.addIssue({
        code: "custom",
        message: `${displayName} must be a whole number.`,
      });
      return z.NEVER;
    }
    if (num < min) {
      ctx.addIssue({
        code: "custom",
        message: `${displayName} must be at least ${min}.`,
      });
      return z.NEVER;
    }
    if (num > max) {
      ctx.addIssue({
        code: "custom",
        message: `${displayName} cannot exceed ${max}.`,
      });
      return z.NEVER;
    }
    return num;
  });
};

/**
 * Creates an optional integer schema with validation for min/max bounds
 * @param options - Configuration options
 * @param options.displayName - Name to use in validation error messages
 * @param options.max - Maximum allowed value
 * @param options.min - Minimum allowed value
 * @returns Zod schema for optional integer with range validation
 *
 * @example
 * // Create schema for age field (must be between 0-120)
 * const ageSchema = createOptionalIntegerSchema({
 *   displayName: "Age",
 *   min: 0,
 *   max: 120
 * });
 *
 * // Invalid inputs fail validation:
 * ageSchema.parse("12.5") // => Error: Age must be a whole number
 * ageSchema.parse("150") // => Error: Age cannot exceed 120
 */
export const createOptionalIntegerSchema = ({
  displayName,
  max,
  min,
}: {
  displayName: string;
  max: number;
  min: number;
}) => {
  return z
    .string()
    .optional()
    .transform((val, ctx) => {
      // Handle undefined and empty strings - these are valid for optional fields
      if (!val || val.trim() === "") {
        return undefined;
      }

      const num = Number(val);
      if (isNaN(num)) {
        ctx.addIssue({
          code: "custom",
          message: "Please enter a valid number.",
        });
        return z.NEVER;
      }
      if (!Number.isInteger(num)) {
        ctx.addIssue({
          code: "custom",
          message: `${displayName} must be a whole number.`,
        });
        return z.NEVER;
      }
      if (num < min) {
        ctx.addIssue({
          code: "custom",
          message: `${displayName} must be at least ${min}.`,
        });
        return z.NEVER;
      }
      if (num > max) {
        ctx.addIssue({
          code: "custom",
          message: `${displayName} cannot exceed ${max}.`,
        });
        return z.NEVER;
      }
      return num;
    });
};
